import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const smoothPointerRef = useRef({ x: 0, y: 0 });
  const animationFrameRef = useRef<number | undefined>(undefined);
  const [isVisible, setIsVisible] = useState(true);

  // Memoize waves configuration with cinematic depth layers
  const waves = useMemo(() => [
    { 
      amplitude: 45, 
      frequency: 0.008, 
      speed: 0.015, 
      color: 'rgba(56, 189, 248, 0.15)',
      blur: 2,
      opacity: 0.3
    },
    { 
      amplitude: 35, 
      frequency: 0.012, 
      speed: 0.02, 
      color: 'rgba(99, 102, 241, 0.12)',
      blur: 1.5,
      opacity: 0.25
    },
    { 
      amplitude: 25, 
      frequency: 0.018, 
      speed: 0.025, 
      color: 'rgba(168, 85, 247, 0.08)',
      blur: 1,
      opacity: 0.2
    }
  ], []);

  // Memoize animation variants
  const containerVariants = useMemo(() => ({
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  }), []);

  const itemVariants = useMemo(() => ({
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  }), []);

  // Memoize navigate handler
  const handleNavigate = useCallback(() => {
    navigate('/credits');
  }, [navigate]);

  // Fade in as section comes into view
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end end"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [0, 1]);
  const smoothOpacity = useSpring(opacity, { stiffness: 100, damping: 20 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Intersection Observer to pause animation when off-screen
    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries[0].isIntersecting);
      },
      { threshold: 0 }
    );
    observer.observe(canvas);

    const resizeCanvas = () => {
      // Lower resolution on mobile for better performance
      const scale = window.innerWidth < 768 ? 0.75 : 1;
      canvas.width = window.innerWidth * scale;
      canvas.height = window.innerHeight * scale;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Throttle pointer tracking for better performance
    let lastPointerUpdate = 0;
    const handlePointerMove = (event: PointerEvent) => {
      const now = Date.now();
      if (now - lastPointerUpdate < 16) return; // ~60fps
      lastPointerUpdate = now;

      const rect = canvas.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      pointerRef.current = {
        x: x * 2 - 1, // normalize to -1..1
        y: y * 2 - 1,
      };
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });

    // Audio wave visualization - use memoized waves
    let time = 0;

    const drawWave = (wave: typeof waves[0], yOffset: number) => {
      const interactiveYOffset = yOffset + smoothPointerRef.current.y * 30;
      const interactiveTilt = smoothPointerRef.current.x * 0.005;

      // Apply depth-based blur effect
      ctx.save();
      ctx.filter = `blur(${wave.blur}px)`;
      
      // Create gradient for depth effect
      const gradient = ctx.createLinearGradient(0, canvas.height / 2 - 50, 0, canvas.height / 2 + 50);
      gradient.addColorStop(0, wave.color.replace(/[\d.]+\)/, '0)'));
      gradient.addColorStop(0.5, wave.color);
      gradient.addColorStop(1, wave.color.replace(/[\d.]+\)/, '0)'));
      
      ctx.beginPath();
      ctx.moveTo(0, canvas.height / 2 + interactiveYOffset);

      for (let x = 0; x <= canvas.width; x++) {
        const depthFactor = 1 - Math.abs(x - canvas.width / 2) / (canvas.width / 2) * 0.3;
        const y = canvas.height / 2 + interactiveYOffset +
          Math.sin((x * wave.frequency) + time * wave.speed + interactiveTilt * x) * wave.amplitude * depthFactor +
          Math.sin(x * wave.frequency * 2 + time * wave.speed * 1.5) * (wave.amplitude * 0.3) * depthFactor;
        ctx.lineTo(x, y);
      }

      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    };

    const animate = () => {
      // Only animate if visible
      if (!isVisible) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      // Enhanced cinematic fade with depth layers
      ctx.fillStyle = 'rgba(0, 0, 0, 0.08)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add subtle vignette effect
      const vignetteGradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, canvas.width * 0.3,
        canvas.width / 2, canvas.height / 2, canvas.width * 0.8
      );
      vignetteGradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
      vignetteGradient.addColorStop(1, 'rgba(0, 0, 0, 0.15)');
      ctx.fillStyle = vignetteGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Ease pointer influence
      smoothPointerRef.current.x += (pointerRef.current.x - smoothPointerRef.current.x) * 0.05;
      smoothPointerRef.current.y += (pointerRef.current.y - smoothPointerRef.current.y) * 0.05;

      // Draw multiple audio waves
      waves.forEach((wave, index) => {
        drawWave(wave, (index - waves.length / 2) * 40);
      });

      // Draw main waveform with enhanced cinematic effects
      ctx.save();
      
      // Create depth gradient for main waveform
      const mainGradient = ctx.createLinearGradient(0, canvas.height / 2 - 60, 0, canvas.height / 2 + 60);
      mainGradient.addColorStop(0, 'rgba(56, 189, 248, 0)');
      mainGradient.addColorStop(0.3, 'rgba(56, 189, 248, 0.3)');
      mainGradient.addColorStop(0.5, 'rgba(56, 189, 248, 0.4)');
      mainGradient.addColorStop(0.7, 'rgba(56, 189, 248, 0.3)');
      mainGradient.addColorStop(1, 'rgba(56, 189, 248, 0)');
      
      ctx.beginPath();
      for (let x = 0; x <= canvas.width; x += 3) {
        const cinematicDepth = 1 - Math.abs(x - canvas.width / 2) / (canvas.width / 2) * 0.4;
        const y = canvas.height / 2 +
          Math.sin(x * 0.02 + time * 0.02 + smoothPointerRef.current.x * 0.5) * (35 + smoothPointerRef.current.y * 12) * cinematicDepth +
          Math.cos(x * 0.01 + time * 0.015) * 25 * cinematicDepth +
          Math.sin(x * 0.005 + time * 0.008) * 15 * cinematicDepth; // Additional harmonic

        if (x === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      
      // Apply subtle glow effect
      ctx.shadowColor = 'rgba(56, 189, 248, 0.5)';
      ctx.shadowBlur = 8;
      ctx.strokeStyle = mainGradient;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      
      // Secondary pass with reduced opacity for depth
      ctx.shadowColor = 'transparent';
      ctx.strokeStyle = 'rgba(56, 189, 248, 0.1)';
      ctx.lineWidth = 4;
      ctx.stroke();
      
      ctx.restore();

      time += 1;
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('pointermove', handlePointerMove);
      observer.disconnect();
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible]);

  return (
    <motion.section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950"
      style={{ opacity: smoothOpacity }}
    >
      {/* Animated Canvas Background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 z-0"
      />

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black opacity-80 z-10" />

      {/* Cinematic Light Flares */}
      <motion.div
        className="absolute rounded-full blur-3xl pointer-events-none"
        style={{
          width: '32rem',
          height: '32rem',
          background: 'radial-gradient(circle, rgba(56,189,248,0.18) 0%, rgba(9,9,11,0) 70%)',
          top: '-5rem',
          left: '-10rem',
          zIndex: 11
        }}
        animate={{ x: [-40, 60, -20], y: [0, 40, -30], rotate: [0, 8, -6] }}
        transition={{ duration: 20, repeat: Infinity, repeatType: 'mirror' }}
      />
      <motion.div
        className="absolute rounded-full blur-3xl pointer-events-none"
        style={{
          width: '36rem',
          height: '36rem',
          background: 'radial-gradient(circle, rgba(129,140,248,0.15) 0%, rgba(9,9,11,0) 65%)',
          bottom: '-6rem',
          right: '-8rem',
          zIndex: 11
        }}
        animate={{ x: [40, -50, 10], y: [10, -30, 20], rotate: [0, -10, 5] }}
        transition={{ duration: 24, repeat: Infinity, repeatType: 'mirror' }}
      />

      {/* Film Grain Overlay */}
      <motion.div
        className="absolute inset-0 pointer-events-none mix-blend-soft-light"
        style={{
          zIndex: 12,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px)',
          backgroundSize: '4px 4px'
        }}
        animate={{ opacity: [0.08, 0.18, 0.08] }}
        transition={{ duration: 5, repeat: Infinity, repeatType: 'mirror' }}
      />

      {/* Letterbox Vignette */}
      <div className="pointer-events-none absolute top-0 inset-x-0 h-28 bg-gradient-to-b from-black via-black/80 to-transparent" style={{ zIndex: 13 }} />
      <div className="pointer-events-none absolute bottom-0 inset-x-0 h-28 bg-gradient-to-t from-black via-black/80 to-transparent" style={{ zIndex: 13 }} />

      {/* Content */}
      <motion.div
        className="relative z-20 text-center px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h1
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-6 bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent"
          variants={itemVariants}
        >
          <span className="block">SANGAM</span>
          <span className="block">PANTA</span>
        </motion.h1>

        <motion.div
          className="text-xl sm:text-2xl md:text-3xl text-gray-300 mb-8 font-light"
          variants={itemVariants}
        >
          <motion.span
            className="inline-block"
            whileHover={{ scale: 1.05, color: "#60a5fa" }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            Sound Designer
          </motion.span>
          <span className="mx-2 text-sky-400">•</span>
          <motion.span
            className="inline-block"
            whileHover={{ scale: 1.05, color: "#60a5fa" }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            Music Composer
          </motion.span>
          <span className="mx-2 text-sky-400">•</span>
          <motion.span
            className="inline-block"
            whileHover={{ scale: 1.05, color: "#60a5fa" }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            Film Projects Supervisor
          </motion.span>
        </motion.div>

        <motion.p
          className="text-lg sm:text-xl text-gray-400 mb-12 max-w-3xl mx-auto leading-relaxed"
          variants={itemVariants}
        >
          Crafting immersive audio experiences that bring stories to life through innovative sound design and original musical compositions.
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          variants={itemVariants}
        >
          <motion.button
            className="px-8 py-4 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold rounded-full shadow-lg transition-all duration-300 text-lg self-center relative overflow-hidden backdrop-blur-sm"
            whileHover={{
              scale: 1.02,
              translateY: -2,
              boxShadow: "0 8px 25px rgba(56, 189, 248, 0.4)"
            }}
            whileTap={{ scale: 0.98, translateY: 0 }}
            onClick={handleNavigate}
          >
            <span className="relative z-10">Explore My Work</span>
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full hover:translate-x-full transition-transform duration-500 ease-out"></div>
          </motion.button>
        </motion.div>
      </motion.div>

          </motion.section>
  );
};
